import {
  AfterViewInit,
  Component,
  ComponentRef,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  inputBinding,
  OnDestroy,
  OnInit,
  outputBinding,
  runInInjectionContext,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { Button } from '@shared/components/button/button';
import { CodeEditor } from '@shared/components/code-editor/code-editor';
import { Select, SelectItem } from '@shared/components/select/select';
import { LanguageBoilerplate } from '@shared/models/language-boilerplate';
import { Problem, ProblemBoilerplate } from '@shared/models/problem';
import { TestCase } from '@shared/models/test-case';
import Quill from 'quill';
import { TestCaseItem } from '../test-case-item/test-case-item';

@Component({
  selector: 'app-view-page',
  imports: [Select, TestCaseItem, Button],
  templateUrl: './view-page.html',
})
export class ViewPage implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiClient = inject(ApiClient);
  private readonly injector = inject(EnvironmentInjector);
  private hasSavedInitialBoilerplate = false;

  // Problem
  private problemId = '';
  readonly problemData = signal<Problem | null>(null);

  // Tab
  readonly selectedTab = signal<string>('initial-code');

  // Test case
  readonly testCases = signal<TestCase[]>([]);

  // Code editor
  readonly codeEditorLanguage = signal<string>('');
  readonly codeEditorLanguageId = signal<string>('');
  readonly CodeEditorInputCode = signal<string>('');
  readonly CodeEditorOutputCode = signal<string>('');
  private codeEditorRef: ComponentRef<CodeEditor> | null = null;
  readonly codeEditorContainer = viewChild('codeEditorOutlet', {
    read: ViewContainerRef,
  });

  // Selector [Difficulty]
  private readonly DIFFICULTY_OPTIONS: SelectItem[] = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];
  readonly selectDifficultyItems = signal<SelectItem[]>(
    this.DIFFICULTY_OPTIONS
  );
  readonly selectedDifficulty = signal<SelectItem>(this.DIFFICULTY_OPTIONS[0]);

  // Selector [Language]
  private readonly LANGUAGE_OPTIONS: SelectItem[] = [
    { value: '63', label: 'javascript' },
    { value: '74', label: 'typescript' },
    { value: '71', label: 'python' },
  ];
  readonly selectLanguageItems = signal<SelectItem[]>(this.LANGUAGE_OPTIONS);
  readonly selectedLanguage = signal<SelectItem>(this.LANGUAGE_OPTIONS[0]);

  // Quill
  private quill!: Quill;
  readonly quillEditorContainer = viewChild('quillEditorContainer', {
    read: ElementRef,
  });
  readonly quillContent = signal<string>('');

  // Form validation
  readonly isVerify = signal<boolean>(false);
  readonly isQuillContentValid = signal<boolean>(false);
  readonly isInitialCodeValid = signal<boolean>(false);

  ngOnInit() {
    this.route.paramMap.subscribe(async params => {
      const id = params.get('problemId') || '';

      if (!id) {
        this.router.navigate(['/problems']);
        return;
      }

      this.problemId = id;
      const response = await this.apiClient.getProblemDataById(id);

      this.problemData.set(response); // set data

      // set difficulty of selector
      this.selectedDifficulty.set(
        this.DIFFICULTY_OPTIONS.find(
          option => option.value === response.difficulty
        ) || this.DIFFICULTY_OPTIONS[0]
      );

      // set quill
      if (this.quill && this.quill.root.innerHTML !== response.description) {
        this.quill.root.innerHTML = response.description;
      }

      // set test case of problem
      const testCaseProblem = response.testCases || [];
      const mappedTestCase = testCaseProblem.map((tc, index) => ({
        id: index + 1,
        input: tc.input,
        expectedOutput: tc.expectedOutput,
        timeLimit: tc.timeLimit ?? null,
        memoryLimit: tc.memoryLimit ?? null,
        isExpanded: false,
        isVisible: tc.isVisible,
      }));
      this.testCases.set(mappedTestCase);
    });

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (!this.hasSavedInitialBoilerplate) {
          const languageBoilerplate = this.problemData()?.languageBoilerplate;

          if (languageBoilerplate) this.hasSavedInitialBoilerplate = true;

          this.saveAllLanguageBoilerplatesToLocalStorage(
            this.problemData()?.languageBoilerplate ?? []
          );
        }

        this.codeEditorLanguage.set(this.selectedLanguage().label || '');
        this.codeEditorLanguageId.set(this.selectedLanguage().value || '');

        if (this.selectedTab() === 'verification-code') {
          this.CodeEditorInputCode.set(
            this.getVerifyCode(Number(this.codeEditorLanguageId()))
          );
        } else if (this.selectedTab() === 'initial-code') {
          this.CodeEditorInputCode.set(
            this.getBaseCode(Number(this.codeEditorLanguageId()))
          );
        }
        this.setUpCodeEditorComponent();
      });
    });
  }

  ngAfterViewInit() {
    const el = this.quillEditorContainer()?.nativeElement;
    if (el) {
      this.quill = new Quill(el, {
        theme: 'snow',
        modules: {
          toolbar: false,
        },
        readOnly: true,
        bounds: el,
      });

      this.quill.on('text-change', () => {
        const htmlContent = this.quill.root.innerHTML;
        this.quillContent.set(htmlContent);
      });
    }
  }

  ngOnDestroy() {
    const existingBoiler = localStorage.getItem('languageBoiler');
    if (!existingBoiler) return;
    let parsedBoiler: Problem[] = [];
    try {
      parsedBoiler = JSON.parse(existingBoiler) as Problem[];
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return;
    }

    // ดึงเฉพาะ languageBoilerPlate ของ problemId ปัจจุบัน
    const currentProblemIndex = parsedBoiler.findIndex(
      p => p.problemId === this.problemId
    );

    if (currentProblemIndex === -1) {
      console.error(
        'No boilerplate found for current problemId:',
        this.problemId
      );
      return;
    }
    // ลบ boilerplate ออกจาก localStorage หลังบันทึกสำเร็จ
    parsedBoiler.splice(currentProblemIndex, 1);
    localStorage.setItem('languageBoiler', JSON.stringify(parsedBoiler));
  }

  // === Tab Management ===
  handleSelectTab(options: string) {
    this.selectedTab.set(options);

    // Destroy and recreate code editor when switching to code tabs
    if (options === 'initial-code' || options === 'verification-code') {
      this.destroyCodeEditor();
    }
  }

  // === Code Editor Management ===
  private setUpCodeEditorComponent() {
    const container = this.codeEditorContainer();
    if (!container || this.codeEditorRef) return;

    if (
      this.selectedTab() !== 'initial-code' &&
      this.selectedTab() !== 'verification-code'
    ) {
      return;
    }

    this.codeEditorRef = container.createComponent(CodeEditor, {
      bindings: [
        inputBinding('language', this.codeEditorLanguage),
        inputBinding('codeInput', this.CodeEditorInputCode),
        inputBinding('readOnly', signal(true)),
        outputBinding('codeOutput', this.CodeEditorOutputCode),
      ],
    });
  }

  private destroyCodeEditor() {
    if (this.codeEditorRef) {
      this.codeEditorRef.destroy();
      this.codeEditorRef = null;
    }
  }

  goToProblems() {
    this.router.navigate(['/problems']);
  }

  private getBaseCode(langId: number): string {
    const existingData = localStorage.getItem('languageBoiler');
    if (!existingData) return '';

    try {
      const problemBoilerplates: ProblemBoilerplate[] =
        JSON.parse(existingData);
      const currentProblem = problemBoilerplates.find(
        p => p.problemId === this.problemId
      );
      if (!currentProblem) return '';

      const entry = currentProblem.languageBoilerplate.find(
        p => p.langId === langId
      );
      return entry?.starterCode ?? '';
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return '';
    }
  }

  // === Verify Code Management ===
  private getVerifyCode(langId: number): string {
    const existingData = localStorage.getItem('languageBoiler');
    if (!existingData) return '';

    try {
      const problemBoilerplates: ProblemBoilerplate[] =
        JSON.parse(existingData);
      const currentProblem = problemBoilerplates.find(
        p => p.problemId === this.problemId
      );
      if (!currentProblem) return '';

      const entry = currentProblem.languageBoilerplate.find(
        p => p.langId === langId
      );
      return entry?.verifyCode ?? '';
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return '';
    }
  }

  // === Helper Methods ===
  private saveAllLanguageBoilerplatesToLocalStorage(
    languageBoilerplate: LanguageBoilerplate[]
  ) {
    const problemId = this.problemId;

    if (languageBoilerplate) {
      const existingData = localStorage.getItem('languageBoiler');
      let boilerplates: ProblemBoilerplate[] = [];

      try {
        if (existingData) {
          boilerplates = JSON.parse(existingData) as ProblemBoilerplate[];
        }
      } catch {
        boilerplates = [];
      }

      const index = boilerplates.findIndex(b => b.problemId === problemId);

      if (index >= 0) {
        boilerplates[index].languageBoilerplate = languageBoilerplate;
      } else {
        boilerplates.push({
          problemId,
          languageBoilerplate,
        });
      }

      localStorage.setItem('languageBoiler', JSON.stringify(boilerplates));
    }
  }
}
